import { NativeDateAdapter } from '@angular/material/core';

/**
 * Date adapter that keeps every date at UTC midnight so a calendar day survives the round trip to the API
 * regardless of the researcher's timezone.
 *
 * Because dates are stored in UTC, EVERY inherited method that reads or builds a date must be overridden to
 * work in UTC too. `NativeDateAdapter` builds dates at *local* midnight (`_createDateWithOverflow`) and reads
 * them back with *local* getters (`_format`), so any method left inherited reads a UTC-midnight date as the
 * wrong calendar day — e.g. `getNumDaysInMonth` returned 30 for a 31-day month east of UTC, which dropped the
 * last day from the calendar grid.
 */
export class DateAdapterService extends NativeDateAdapter {
  override createDate(year: number, month: number, date: number): Date {
    return new Date(Date.UTC(year, month, date));
  }

  override today(): Date {
    const now = new Date();
    return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  }

  override getYear(date: Date): number {
    return date.getUTCFullYear();
  }

  override getMonth(date: Date): number {
    return date.getUTCMonth();
  }

  override getDate(date: Date): number {
    return date.getUTCDate();
  }

  override getDayOfWeek(date: Date): number {
    return date.getUTCDay();
  }

  override getNumDaysInMonth(date: Date): number {
    // Day 0 of the next month is the last day of this one.
    return new Date(Date.UTC(this.getYear(date), this.getMonth(date) + 1, 0)).getUTCDate();
  }

  override addCalendarMonths(date: Date, months: number): Date {
    const targetMonth = this.getMonth(date) + months;
    const daysInTargetMonth = new Date(Date.UTC(this.getYear(date), targetMonth + 1, 0)).getUTCDate();

    // Clamp so 31 Jan + 1 month lands on the last day of February rather than overflowing into March.
    return new Date(Date.UTC(this.getYear(date), targetMonth, Math.min(this.getDate(date), daysInTargetMonth)));
  }

  override addCalendarDays(date: Date, days: number): Date {
    return new Date(Date.UTC(this.getYear(date), this.getMonth(date), this.getDate(date) + days));
  }

  override format(date: Date, displayFormat: object): string {
    if (!this.isValid(date)) {
      throw Error('DateAdapterService: Cannot format invalid date.');
    }

    return new Intl.DateTimeFormat(this.locale, { ...displayFormat, timeZone: 'UTC' }).format(date);
  }

  override getYearName(date: Date): string {
    return new Intl.DateTimeFormat(this.locale, { year: 'numeric', timeZone: 'UTC' }).format(date);
  }

  override parse(value: unknown, parseFormat?: unknown): Date | null {
    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return null;
      }

      if (trimmedValue.includes('/')) {
        return this.parseDayFirstDate(trimmedValue);
      }
    }

    return super.parse(value, parseFormat);
  }

  private parseDayFirstDate(value: string): Date | null {
    const [dayValue, monthValue, yearValue] = value.split('/');
    const day = Number(dayValue);
    const month = Number(monthValue);
    const year = Number(yearValue);

    if (!dayValue || !monthValue || !yearValue || yearValue.length !== 4) {
      return null;
    }

    const parsedDate = new Date(Date.UTC(year, month - 1, day));
    const isSameDate = parsedDate.getUTCFullYear() === year && parsedDate.getUTCMonth() === month - 1 && parsedDate.getUTCDate() === day;

    return isSameDate ? parsedDate : null;
  }
}
